import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface UseInactivityLogoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
}

export function useInactivityLogout({ 
  timeoutMinutes = 10080, // 7 days
  warningMinutes = 10050  // Warning 30 minutes before
}: UseInactivityLogoutOptions = {}) {
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showingWarning, setShowingWarning] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const warningRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const resetTimer = () => {
      setLastActivity(Date.now());
      setShowingWarning(false);

      // Clear existing timers
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);

      // Set warning timer
      warningRef.current = setTimeout(() => {
        if (!showingWarning) {
          setShowingWarning(true);
          toast({
            title: "Inactivity Warning",
            description: `You will be logged out in ${timeoutMinutes - warningMinutes} minutes due to inactivity.`,
            duration: 10000,
          });
        }
      }, warningMinutes * 60 * 1000);

      // Set logout timer
      timeoutRef.current = setTimeout(async () => {
        await signOut();
        toast({
          title: "Logged Out",
          description: "You were logged out due to inactivity.",
          variant: "destructive",
        });
      }, timeoutMinutes * 60 * 1000);
    };

    // Initialize timers
    resetTimer();

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, signOut, toast, timeoutMinutes, warningMinutes, showingWarning]);

  return { lastActivity };
}